/** This month's headline figures: the inventory page's top row and the dashboard's. */
import { currentLocale, t } from "../i18n/index.ts";
import { money, percent, todayIn } from "../lib/format.ts";
import { useFinance } from "../lib/queries.ts";
import { useWs } from "../lib/workspace.tsx";

export function monthBounds(today: string, back: number): { from: string; to: string; name: string } {
  const [y, m] = today.split("-").map(Number) as [number, number];
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  const first = new Date(Date.UTC(y, m - 1 - back, 1));
  const last = new Date(Date.UTC(y, m - back, 0));
  const name = first.toLocaleDateString(currentLocale(), { month: "long", timeZone: "UTC" });
  return { from: iso(first), to: back === 0 ? today : iso(last), name };
}

/**
 * Last month up to the same day of the month as today, so a month in progress
 * is compared like for like (Oct 1–2 against Sep 1–2). Clamped to last
 * month's length: on Oct 31 that's all of September.
 */
export function priorToDate(today: string): { from: string; to: string; label: string } {
  const [y, m, d] = today.split("-").map(Number) as [number, number, number];
  const first = new Date(Date.UTC(y, m - 2, 1));
  const end = new Date(Date.UTC(y, m - 2, Math.min(d, new Date(Date.UTC(y, m - 1, 0)).getUTCDate())));
  const iso = (x: Date) => x.toISOString().slice(0, 10);
  const label = new Intl.DateTimeFormat(currentLocale(), { month: "short", day: "numeric", timeZone: "UTC" }).formatRange(first, end);
  return { from: iso(first), to: iso(end), label };
}

/**
 * This month at a glance, from the same figures as the Finance page. The
 * dashboard adds a fourth tile: what the stock on hand cost.
 */
export function Kpis({ stockValue = false }: { stockValue?: boolean }) {
  const ws = useWs();
  const today = todayIn(ws.timezone);
  const now = monthBounds(today, 0);
  const prev = priorToDate(today);
  const cur = useFinance(ws.id, now.from, now.to, false);
  const last = useFinance(ws.id, prev.from, prev.to, false);
  const d = cur.data;
  if (!d) return null;

  const before = last.data?.unitsSold;
  let trend: { text: string; tone: string } | null = null;
  if (before !== undefined) {
    if (before === 0) trend = { text: t("inventory.kpi.noneLast", { range: prev.label }), tone: "muted" };
    else {
      const change = (d.unitsSold - before) / before;
      const signed = `${change > 0 ? "+" : ""}${percent(change)}`;
      trend = { text: t("inventory.kpi.vsLast", { change: signed, range: prev.label }), tone: change > 0 ? "pos" : change < 0 ? "neg" : "muted" };
    }
  }

  return (
    <div className={`kpis${stockValue ? " kpis-4" : ""}`}>
      <div className="kpi">
        <span className="label">{t("inventory.kpi.listed")}</span>
        <b className="num">{d.inventory.listedUnits}</b>
        <em className="muted">{t("inventory.kpi.inStock", { count: d.inventory.activeUnits - d.inventory.listedUnits })}</em>
      </div>
      <div className="kpi">
        <span className="label">{t("inventory.kpi.sold", { month: now.name })}</span>
        <b className="num">{d.unitsSold}</b>
        {trend && <em className={trend.tone}>{trend.text}</em>}
      </div>
      <div className="kpi">
        <span className="label">{t("inventory.kpi.profit", { month: now.name })}</span>
        <b className={`num${d.netProfitMinor < 0 ? " neg" : ""}`}>{money(d.netProfitMinor, ws.homeCurrency)}</b>
        <em className="muted">{t("inventory.kpi.afterCosts")}</em>
      </div>
      {stockValue && (
        <div className="kpi">
          <span className="label">{t("finance.capital")}</span>
          <b className="num">{money(d.inventory.capitalAtRestMinor, ws.homeCurrency)}</b>
          <em className="muted">{t("dashboard.kpi.stockCost", { count: d.inventory.activeUnits })}</em>
        </div>
      )}
    </div>
  );
}
